import {
  Component,
  EventEmitter,
  Inject,
  InjectionToken,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  EMPTY,
  finalize,
  Subject,
  Subscription,
  takeUntil,
  tap,
  switchMap,
  merge,
  skip,
  take,
} from 'rxjs';
import {
  MapFilterItem,
  MapFilterType,
} from 'app/shared/components/main-map/components/search-results/map-filter-state.model';
import { MainMapService } from 'app/shared/components/main-map/main-map.service';
import { FiltersService } from 'app/shared/components/main-map/components/search-results/filters.service';
import { LoadingController } from '../../../../helpers/loading.controller';
import { ToastService } from '../../../../services/toast.service';
import { HighlightService } from '../../highlight.service';
import {
  ACTIVE_FILTERS_ON_MAP_MANAGER,
  MapFilterManager,
} from './map-filter.manager';
import { SearchResultsService } from './search-results.service';
import { ScreenService } from 'app/shared/services/screen.service';
import {
  animate,
  animateChild,
  group,
  query,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { TranslationService } from '@services/translation.service';

export const SEARCH_LOCAL_LOADING = new InjectionToken<LoadingController>(
  'SEARCH_LOCAL_LOADING'
);

interface SideMenuItem {
  text: string;
  onClick?: () => void;
}

enum ResultsViewType {
  Table,
  Chart,
  Statistics,
}

const closedStateStyle = style({
  transform: 'translateX(calc(-1 * var(--side-content-width)))',
  'padding-right': '0px',
});

const transitionToggleOpenedAnim = animate('300ms ease-in-out');

@Component({
  selector: 'app-search-results',
  templateUrl: 'search-results.component.html',
  styleUrls: ['search-results.component.scss'],
  providers: [
    FiltersService,
    {
      provide: MapFilterManager,
      useClass: MapFilterManager,
    },
    {
      provide: ACTIVE_FILTERS_ON_MAP_MANAGER,
      useClass: MapFilterManager,
    },
    {
      provide: SEARCH_LOCAL_LOADING,
      useFactory: () => new LoadingController(),
    },
    SearchResultsService,
  ],
  animations: [
    trigger('handleIndicatorOpened', [
      state(
        'false',
        style({
          transform: 'rotate(180deg)',
        })
      ),
      state(
        'true',
        style({
          transform: 'rotate(0deg)',
        })
      ),
      transition('* <=> *', transitionToggleOpenedAnim),
    ]),
    trigger('handleSideMenuOpened', [
      state(
        'true',
        style({
          transform: 'translateX(0%)',
          'padding-right': 'var(--side-content-width)',
        })
      ),
      state('false', closedStateStyle),
      transition(
        '* <=> *',
        group([
          query('[childItemsToAnim]', animateChild(), { optional: true }),
          transitionToggleOpenedAnim,
        ])
      ),
    ]),
  ],
})
export class SearchResultsComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  mapView: __esri.MapView;
  @Input()
  mainMapImageLayer: __esri.MapImageLayer;

  @Input()
  geometryToSearch: __esri.Geometry;
  @Output()
  geometryToSearchChange = new EventEmitter<__esri.Geometry>();

  @Input()
  floatingFilterContainer: Element;
  floatingFilterVisible = false;

  fieldsObservable = this.searchResultsService.onFieldsLoaded;

  @Output()
  onGraphicZoomed = new EventEmitter<any>();
  @Output()
  onFilterOnMapClick = new EventEmitter<any>();

  @Output()
  onClearClick = new EventEmitter<any>();

  sideMenuItems: SideMenuItem[];
  sideMenuOpened: boolean;

  resultsViewTypes = ResultsViewType;
  resultsViewTypeSelected = ResultsViewType.Table;

  tableViewPageIndex: number;
  chartViewFieldSelected: __esri.Field;
  statisticsViewFieldSelected: __esri.Field;
  statisticsViewPageIndex: number;

  filterItemList: MapFilterItem[];

  get isFilterDisabled() {
    return !(this.filterItemList?.length || this.geometryToSearch);
  }

  localizationFilterModalVisible: boolean;
  entityTypeFilterModalVisible: boolean;
  objectTypeFilterModalVisible: boolean;
  entityFilterModalVisible: boolean;
  attributesFilterModalVisible: boolean;
  objectIdFilterModalVisible: boolean;

  saveFilterModalVisible: boolean;
  filterHistoryModalVisible: boolean;

  exportConfirmModalVisible: boolean;

  private highlightSearchResultGeomSubject = new Subject<__esri.Geometry>();

  canExportObservable =
    this.searchResultsService.observeCanExportSearchResults();

  canExportShpObservable =
    this.searchResultsService.observeCanExportShpSearchResults();

  isMobileMode: boolean;

  private mapViewSubscription: Subscription;

  private lifecycle = new Subject<void>();

  constructor(
    private mainMapService: MainMapService,
    private searchResultsService: SearchResultsService,
    private mapFilterManager: MapFilterManager,
    @Inject(ACTIVE_FILTERS_ON_MAP_MANAGER)
    private activeFiltersOnMapManager: MapFilterManager,
    private highlightService: HighlightService,
    @Inject(SEARCH_LOCAL_LOADING) public loadingController: LoadingController,
    private globalLoadingController: LoadingController,
    private toastService: ToastService,
    private screenService: ScreenService,
    private ts: TranslationService
  ) {}

  ngOnInit(): void {
    const mobileModeObservable = this.screenService.observeIsMobile();

    merge(
      mobileModeObservable.pipe(
        take(1),
        tap((isMobile) => {
          this.sideMenuOpened = !isMobile;
        })
      ),
      mobileModeObservable.pipe(skip(1))
    )
      .pipe(takeUntil(this.lifecycle))
      .subscribe((isMobile) => {
        this.isMobileMode = isMobile;
      });

    this.sideMenuItems = [
      {
        text: 'Localização',
        onClick: () => {
          this.localizationFilterModalVisible = true;
        },
      },
      {
        text: 'Tipo de entidade',
        onClick: () => {
          this.entityTypeFilterModalVisible = true;
        },
      },
      {
        text: 'Entidade',
        onClick: () => {
          this.entityFilterModalVisible = true;
        },
      },
      {
        text: 'Objeto',
        onClick: () => {
          this.objectTypeFilterModalVisible = true;
        },
      },
      {
        text: 'Elemento de caraterização',
        onClick: () => {
          this.attributesFilterModalVisible = true;
        },
      },
      {
        text: 'Id objeto',
        onClick: () => {
          this.objectIdFilterModalVisible = true;
        },
      },
    ];

    this.mapFilterManager.onStateLoaded
      .pipe(takeUntil(this.lifecycle))
      .subscribe((state) => {
        if (state?.filters?.length) {
          this.searchResultsService.enableSearch();
        }
      });

    this.mapFilterManager.onFiltersChanged
      .pipe(takeUntil(this.lifecycle))
      .subscribe((filters) => {
        this.filterItemList = filters;
      });

    this.activeFiltersOnMapManager.onFiltersChanged
      .pipe(takeUntil(this.lifecycle))
      .subscribe((filters) => {
        if (!filters?.length) {
          this.floatingFilterVisible = false;
        }
      });

    this.searchResultsService.onPreparationChanged
      .pipe(takeUntil(this.lifecycle))
      .subscribe(() => {
        this.statisticsViewPageIndex = 0;
        this.tableViewPageIndex = 0;
      });

    this.searchResultsService.onCleared
      .pipe(takeUntil(this.lifecycle))
      .subscribe(() => {
        this.tableViewPageIndex = 0;
        this.chartViewFieldSelected = null;
        this.statisticsViewFieldSelected = null;
        this.statisticsViewPageIndex = 0;

        this.highlightSearchResultGeomSubject.next(null);

        if (this.geometryToSearch) {
          this.geometryToSearch = null;
          this.geometryToSearchChange.emit(this.geometryToSearch);
        }
      });

    this.mainMapService.onRequestClear
      .pipe(takeUntil(this.lifecycle))
      .subscribe(() => {
        this.searchResultsService.clear();
      });

    this.highlightSearchResultGeomSubject
      .pipe(
        switchMap((geometry) => {
          if (!geometry) {
            return EMPTY;
          }

          return this.highlightService.highlight(geometry);
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.mapView || changes.mainMapImageLayer) {
      this.searchResultsService.setup(this.mapView, this.mainMapImageLayer);
    }

    if (
      changes.geometryToSearch &&
      (!changes.geometryToSearch.firstChange ||
        (changes.geometryToSearch.firstChange && this.geometryToSearch))
    ) {
      this.searchResultsService.setGeometry(this.geometryToSearch);
      if (this.geometryToSearch) {
        this.searchResultsService.enableSearch();
      } else {
        this.searchResultsService.disableSearch();
      }
    }
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();

    this.sideMenuItems = null;

    this.mapViewSubscription?.unsubscribe();
  }

  onGraphicZoomedInternal(res) {
    this.highlightSearchResultGeomSubject.next(res?.item?.geometry);
    this.searchResultsService.clearLocalizationHighlight();

    this.onGraphicZoomed.emit(res);
  }

  onFilterItemRemoveClick(item: MapFilterItem) {
    if (
      item.type === MapFilterType.EntityType &&
      item.value !== MapFilterItem.DEFAULT_VALUE_ALL
    ) {
      const filterKeys = this.mapFilterManager.listFilterKeysBy(
        (x) =>
          x.type === MapFilterType.Entity &&
          x.value !== MapFilterItem.DEFAULT_VALUE_ALL
      );

      this.mapFilterManager.removeFilterRangeByKeys(
        [item.id].concat(filterKeys)
      );
    } else {
      this.mapFilterManager.removeFilter(item);
    }
  }

  onBtnFilterOnMapClick() {
    this.searchResultsService.searchOnMap();

    this.onFilterOnMapClick.emit();

    this.floatingFilterVisible = true;
  }

  onBtnFilterClick() {
    this.searchResultsService.enableSearch();
  }

  onBtnClearClick() {
    this.searchResultsService.clear();
    this.onClearClick.emit();
  }

  onBtnExportShpClick() {
    this.searchResultsService
      .shouldShowExportShpSearchResultsLimit()
      .pipe(takeUntil(this.lifecycle))
      .subscribe((aBool) => {
        if (aBool) {
          this.exportConfirmModalVisible = true;
        } else {
          this.exportShp();
        }
      });
  }

  onBtnExportXlsxClick() {
    if (this.globalLoadingController.isShown()) {
      return;
    }

    this.globalLoadingController.show();
    this.searchResultsService
      .exportSearchResults()
      .pipe(
        finalize(() => {
          this.globalLoadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (link) => {
          window.open(link, '_blank');
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.ts.getCurrentLanguage()['failed-export-data']
          );
        },
      });
  }

  onBtnFilterHistoryClick() {
    this.filterHistoryModalVisible = true;
  }

  onBtnOpenSaveFilterModalClick() {
    this.saveFilterModalVisible = true;
  }

  exportShp() {
    if (this.globalLoadingController.isShown()) {
      return;
    }

    this.globalLoadingController.show();
    this.searchResultsService
      .exportSearchResults(true)
      .pipe(
        finalize(() => {
          this.globalLoadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (link) => {
          window.open(link, '_blank');
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.ts.getCurrentLanguage()['failed-export-data']
          );
        },
      });
  }
}
